import { z } from "zod";

// The time window every procurement override carries (lead-time overrides
// today, inventory-policy overrides once they adopt it): starts_at inclusive,
// ends_at exclusive, plus an advisory review date. One rule applies to all of
// them: an override must carry an expiry OR a review date, so nothing lives
// forever unnoticed (docs/procurement/lead-time-overrides.md, decision 2).

export const OverrideWindowShape = {
  /** Defaults to now on the server. */
  startsAt: z.string().datetime().optional(),
  /** Exclusive end; null = open-ended, in which case `reviewAt` is required. */
  endsAt: z.string().datetime().nullable().optional(),
  /** Advisory: the override keeps applying past it and shows as needing review. */
  reviewAt: z.string().datetime().nullable().optional(),
} as const;

export interface OverrideWindowInput {
  startsAt?: string;
  endsAt?: string | null;
  reviewAt?: string | null;
}

export const EXPIRY_OR_REVIEW_REQUIRED_MESSAGE =
  "Set an expiry date or a review date.";
export const WINDOW_ORDER_MESSAGE = "endsAt must be later than startsAt.";

export function hasExpiryOrReview(window: OverrideWindowInput): boolean {
  return window.endsAt != null || window.reviewAt != null;
}

export function endsAfterStart(window: OverrideWindowInput): boolean {
  if (!window.endsAt) return true;
  const start = window.startsAt ? new Date(window.startsAt) : new Date();
  return new Date(window.endsAt).getTime() > start.getTime();
}

/**
 * Applies the shared window rules to any object schema that spreads
 * {@link OverrideWindowShape}. Kept as one function so the two override
 * contracts cannot drift on what "a valid window" means.
 */
export function withOverrideWindowRules<
  S extends z.ZodType<OverrideWindowInput>,
>(schema: S): S {
  return schema
    .refine(hasExpiryOrReview, {
      path: ["endsAt"],
      message: EXPIRY_OR_REVIEW_REQUIRED_MESSAGE,
    })
    .refine(endsAfterStart, {
      path: ["endsAt"],
      message: WINDOW_ORDER_MESSAGE,
    }) as S;
}
